import { currentSeason } from './rotationScore.js'
import { localDayISO, shiftLocalDayISO } from './localDay.js'
import { CANONICAL_WEAR_OCCASIONS, canonicalizeWearOccasion } from './wearOccasions.js'
import { createWeatherSnapshot } from './weatherVocabulary.js'

export const QUICK_WEAR_OCCASIONS = CANONICAL_WEAR_OCCASIONS
export const WEAR_CONTEXT_TIMEOUT_MS = 2000
export const WEAR_CONTEXT_RETRY_DELAY_MS = 2000
export const WEAR_CONTEXT_FRESH_MS = 15 * 60 * 1000

const WORKER_URL = import.meta.env?.VITE_WORKER_URL || 'https://fragrance-fetch.rbarbs23.workers.dev'
const WEATHER_FIELDS = ['temp_c', 'humidity_pct', 'pressure_hpa', 'condition', 'provider', 'fetched_at']

let sessionWeather = null
let sessionWeatherOwner = null
const weatherListeners = new Set()

// Screens that show weather (Home header, Rotation Auto) subscribe here so a
// reading captured in the background reaches them without a reload.
export function subscribeWearWeather(listener) {
  weatherListeners.add(listener)
  return () => weatherListeners.delete(listener)
}

function notifyWearWeather(event) {
  for (const listener of weatherListeners) {
    try { listener(event) } catch { /* a screen must never break capture */ }
  }
}

export function currentSessionWeather(nowMs = Date.now(), ownerId = null) {
  return freshSessionWeather(nowMs, ownerId)
}

export function failedWearContext(capturedAt = new Date().toISOString(), location = null) {
  return {
    context_weather: location ? {
      lat: Math.round(Number(location.lat) * 100) / 100,
      lon: Math.round(Number(location.lon) * 100) / 100,
      captured_at: capturedAt,
    } : {},
    context_captured_at: capturedAt,
    context_source: 'failed',
  }
}

export function clearWearWeatherSessionCache() {
  sessionWeather = null
  sessionWeatherOwner = null
}

function rememberSessionWeather(weather, ownerId) {
  sessionWeather = weather
  sessionWeatherOwner = ownerId || null
}

// The cache belongs to one signed-in account: a different (or unknown)
// account never reads the previous account's IP-derived reading.
function freshSessionWeather(nowMs, ownerId) {
  if (!sessionWeather || !ownerId || ownerId !== sessionWeatherOwner) return null
  const fetchedAt = Date.parse(sessionWeather.fetched_at)
  return Number.isFinite(fetchedAt) && nowMs - fetchedAt <= WEAR_CONTEXT_FRESH_MS
    ? sessionWeather
    : null
}

function normalizeWeatherPayload(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Weather context response is missing')
  }
  const normalized = Object.fromEntries(WEATHER_FIELDS.map(field => [field, value[field]]))
  if (!Number.isFinite(normalized.temp_c)
      || !Number.isFinite(normalized.humidity_pct)
      || !Number.isFinite(normalized.pressure_hpa)
      || typeof normalized.condition !== 'string'
      || typeof normalized.provider !== 'string'
      || Number.isNaN(Date.parse(normalized.fetched_at))) {
    throw new Error('Weather context response is invalid')
  }
  return normalized
}

async function fetchWithTimeout(url, options, {
  fetchImpl = globalThis.fetch,
  timeoutMs = WEAR_CONTEXT_TIMEOUT_MS,
} = {}) {
  const controller = new AbortController()
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      controller.abort()
      reject(new Error('Weather context request timed out'))
    }, timeoutMs)
  })

  try {
    return await Promise.race([
      fetchImpl(url, { ...options, signal: controller.signal }),
      timeout,
    ])
  } finally {
    clearTimeout(timer)
  }
}

async function updateWearContext(supabaseClient, wearLogId, userId, context) {
  const { error } = await supabaseClient
    .from('wear_logs')
    .update(context)
    .eq('id', wearLogId)
    .eq('user_id', userId)
  if (error) throw error
}

// Live local weather for display, independent of whether a wear is logged
// today. Reuses the 15-minute session cache the wear capture already fills.
export async function fetchLiveWeather({
  supabaseClient,
  workerUrl = WORKER_URL,
  fetchImpl = globalThis.fetch,
  timeoutMs = WEAR_CONTEXT_TIMEOUT_MS,
  nowMs = Date.now(),
  expectedOwnerId = null,
} = {}) {
  try {
    const { data, error } = await supabaseClient.auth.getSession()
    if (error) return null
    const accessToken = data?.session?.access_token
    if (!accessToken) return null
    const ownerId = data?.session?.user?.id || null
    // A caller asking on behalf of one account never receives another's reading.
    if (expectedOwnerId && ownerId !== expectedOwnerId) return null
    const cached = freshSessionWeather(nowMs, ownerId)
    if (cached) return cached
    const response = await fetchWithTimeout(`${workerUrl}/weather/context`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    }, { fetchImpl, timeoutMs: Math.max(timeoutMs, 4000) })
    if (!response.ok) return null
    const payload = await response.json().catch(() => ({}))
    const weather = normalizeWeatherPayload(payload?.weather)
    rememberSessionWeather(weather, ownerId)
    notifyWearWeather({ weather, ownerId })
    return weather
  } catch {
    return null
  }
}

export async function captureWearLogContext({
  supabaseClient,
  wearLogId,
  userId,
  workerUrl = WORKER_URL,
  fetchImpl = globalThis.fetch,
  timeoutMs = WEAR_CONTEXT_TIMEOUT_MS,
  now = () => new Date().toISOString(),
  sleep = delay => new Promise(resolve => setTimeout(resolve, delay)),
  retryDelayMs = WEAR_CONTEXT_RETRY_DELAY_MS,
  enableWeatherSnapshot = false,
}) {
  const capturedAt = now()
  let failed = failedWearContext(capturedAt)
  let context = failed

  try {
    const { data, error } = await supabaseClient.auth.getSession()
    if (error) throw error
    const accessToken = data?.session?.access_token
    if (!accessToken) throw new Error('Weather context requires an authenticated session')

    let weather = freshSessionWeather(Date.parse(capturedAt), userId)
    let lastLocation = null
    for (let attempt = 0; !weather && attempt < 2; attempt += 1) {
      if (attempt > 0) await sleep(retryDelayMs)
      try {
        const response = await fetchWithTimeout(`${workerUrl}/weather/context`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        }, { fetchImpl, timeoutMs })
        const payload = await response.json().catch(() => ({}))
        if (payload?.location) lastLocation = payload.location
        if (response.ok) weather = normalizeWeatherPayload(payload?.weather)
      } catch {
        // The retry owns transient transport and timeout failures.
      }
    }
    if (!weather) {
      failed = failedWearContext(capturedAt, lastLocation)
      throw new Error('Weather context unavailable after retry')
    }
    rememberSessionWeather(weather, userId)
    context = {
      context_weather: weather,
      context_captured_at: weather.fetched_at,
      context_source: 'auto_ip',
    }

    // Capture weather snapshot if enabled
    if (enableWeatherSnapshot) {
      const snapshot = createWeatherSnapshot(weather)
      if (snapshot) {
        context.weather_snapshot = snapshot
      }
    }
  } catch {
    // The initial insert already contains this exact failed context. Repeating
    // the owner-scoped update makes this helper safe for older callers too.
    context = failed
  }

  try {
    await updateWearContext(supabaseClient, wearLogId, userId, context)
    if (context.context_source === 'auto_ip') {
      notifyWearWeather({ wearLogId, weather: context.context_weather, context, ownerId: userId })
    }
  } catch {
    // Context enrichment must never surface as a wear-log failure. The row was
    // inserted with a truthful failed marker before this background work began.
  }
  return context
}

export function queueWearLogContextCapture(options) {
  void captureWearLogContext(options).catch(() => {})
}

// Local calendar day — see src/lib/localDay.js. This is the WRITE path: it sets
// wear_date, so a UTC boundary here files an evening wear under tomorrow.
export function todayISO(date = new Date()) {
  return localDayISO(date)
}

export function yesterdayISO() {
  return shiftLocalDayISO(1)
}

export function timeBucket(date = new Date()) {
  const hour = date.getHours()
  if (hour >= 5 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 17) return 'afternoon'
  if (hour >= 17 && hour < 22) return 'evening'
  return 'night'
}

export function buildWearLogEntry(fragrance, {
  date = todayISO(),
  occasion = '',
  note = '',
  sprays = null,
  season = '',
  timeBucket: selectedTimeBucket = '',
  eveningContext = '',
  layeredWith = [],
} = {}) {
  const wornAt = new Date(`${date}T12:00:00`)
  const resolvedSeason = season || currentSeason(wornAt)
  const cleanOccasion = canonicalizeWearOccasion(occasion)
  const cleanNote = note.trim() || null

  return {
    fragrance_id: fragrance.id,
    fragrance_name: fragrance.name,
    house: fragrance.house,
    worn_on: date,
    time_bucket: selectedTimeBucket || timeBucket(new Date()),
    day_of_week: wornAt.getDay(),
    season: resolvedSeason,
    occasion: cleanOccasion,
    sprays: sprays === '' || sprays == null ? null : Number(sprays),
    evening_context: eveningContext || null,
    layered_with: layeredWith,
    note: cleanNote,
    ...failedWearContext(),
  }
}

export function wearDate(log) {
  return log?.worn_on || log?.wear_date || null
}

export function logOccasion(log) {
  return log?.occasion || log?.context || ''
}

export function logNote(log) {
  return log?.note || log?.notes || ''
}
